import { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Divider, Text } from 'components/ui';
import localeUtils from 'utils/LanguageLocale';
import ProductCompareTool from 'components/ProductPage/ProductCompareTool';
import ProductList from 'components/Content/ProductList';
import SimilarProductFilters from './SimilarProductFilters.f';
import { useFilteredRecommendations } from './useFilteredRecommendations';
import bpRedesignedUtils from 'utils/BeautyPreferencesRedesigned';
import recServiceUtils from 'utils/RecommendationService';
import productUtils from 'utils/product';
import pdpCompareUtils from 'utils/pdpCompare';
import { SIMPLIFIED_PDP_COMPARE } from 'constants/TestTarget';
import { CONSTRUCTOR_PODS, GROUPING } from 'constants/constructorConstants';
import SimilarProductCarouselEN from './locales/SimilarProductCarousel_en_US';
import SimilarProductCarouselFR from './locales/SimilarProductCarousel_fr_CA';

const { transformRecommendationsForCarousel } = recServiceUtils;

const SIMPLIFIED_COMPARE_ATTRIBUTES = ['price', 'rating'];

const SimilarProductCarousel = ({ isSmallView, currentProduct, simplifiedCompareVersion }) => {
    const isSimplified = !!simplifiedCompareVersion;
    const isToggleable = simplifiedCompareVersion === SIMPLIFIED_PDP_COMPARE.CHALLENGER_FOUR;
    const podIdOverride = isSimplified ? CONSTRUCTOR_PODS.SIMILAR_PRODUCTS : null;
    const { podId, productRecommendationsData, isFetching, updateFilter } = useFilteredRecommendations(currentProduct, podIdOverride);
    const { recommendations = [], resultId = '', totalNumResults = 0 } = productRecommendationsData;
    const recommendationsForCarousel = transformRecommendationsForCarousel(recommendations);

    const [showComparison, setShowComparison] = useState(false);
    const toggleComparison = useCallback(() => setShowComparison(prev => !prev), []);
    const getText = localeUtils.getLocalisedDataFromResource(SimilarProductCarouselEN, SimilarProductCarouselFR);

    const transformedCurrentProduct = useMemo(
        () => (isToggleable ? pdpCompareUtils.transformProductDataToRecsServiceFormat(currentProduct) : null),
        [currentProduct, isToggleable]
    );

    const carouselSkuList = useMemo(
        () => (transformedCurrentProduct ? [transformedCurrentProduct, ...recommendationsForCarousel] : recommendationsForCarousel),
        [transformedCurrentProduct, recommendationsForCarousel]
    );

    if (isSimplified && !isFetching && recommendationsForCarousel.length === 0) {
        return null;
    }

    const featuresData = currentProduct?.cxsContent?.similarProductsComparison?.featuresData;
    const currentPrice = currentProduct?.currentSku?.listPrice;
    const title = localeUtils.isFRCanada() ? 'Produits similaires' : `Similar ${currentProduct?.parentCategory?.displayName || 'Products'}`;
    const parentCategoryId = productUtils.findProductRootParentCategoryId(currentProduct?.parentCategory);
    const masterlistWorld = bpRedesignedUtils.getMasterListRefinementWorldByCategoryId(parentCategoryId);
    const isCollapsible = simplifiedCompareVersion === SIMPLIFIED_PDP_COMPARE.CHALLENGER_THREE;
    const pdpCompareAttributes =
        isSimplified && !isCollapsible && !isToggleable
            ? SIMPLIFIED_COMPARE_ATTRIBUTES
            : pdpCompareUtils.getPdpCompareAttributes(masterlistWorld?.key);

    const similarProductVariants = isSimplified ? null : (
        <SimilarProductFilters
            featuresData={featuresData}
            selectedVariation={podId}
            onFilterChange={updateFilter}
            currentPrice={currentPrice}
        />
    );

    if (isToggleable) {
        return (
            <Box
                lineHeight='tight'
                marginBottom={[6, 7]}
            >
                <Divider />
                <Box
                    display='flex'
                    justifyContent='space-between'
                    alignItems='center'
                    marginTop={4}
                    marginBottom={2}
                    gap={3}
                >
                    <Text
                        is='h2'
                        fontSize={['md', 'lg']}
                        fontWeight='bold'
                        children={title}
                        css={{ flexGrow: 1, minWidth: 0 }}
                    />
                    <Text
                        is='button'
                        aria-expanded={showComparison}
                        color='blue'
                        fontSize='base'
                        onClick={toggleComparison}
                        css={toggleButtonStyle}
                    >
                        {getText(showComparison ? 'hideComparison' : 'showComparison')}
                    </Text>
                </Box>
                {showComparison ? (
                    <ProductCompareTool
                        skuList={recommendationsForCarousel}
                        similarProductVariants={null}
                        isLoading={isFetching}
                        isSmallView={isSmallView}
                        podId={podId}
                        resultId={resultId}
                        totalResults={totalNumResults}
                        isCarousel
                        pdpCompareAttributes={pdpCompareAttributes}
                        isCollapsible={false}
                        currentProduct={currentProduct}
                        hideHeader
                    />
                ) : (
                    <ProductList
                        skuList={carouselSkuList}
                        grouping={GROUPING.SIMPLIFIED_PDP_COMPARE}
                        podId={podId}
                        resultId={resultId}
                        totalResults={totalNumResults}
                        isCarousel
                        marginTop={0}
                        marginBottom={0}
                    />
                )}
            </Box>
        );
    }

    return (
        <Box
            lineHeight='tight'
            marginBottom={[6, 7]}
        >
            <Divider />
            <ProductCompareTool
                skuList={recommendationsForCarousel}
                similarProductVariants={similarProductVariants}
                isLoading={isFetching}
                isSmallView={isSmallView}
                title={title}
                podId={podId}
                resultId={resultId}
                totalResults={totalNumResults}
                isCarousel
                pdpCompareAttributes={pdpCompareAttributes}
                isCollapsible={isCollapsible}
                currentProduct={currentProduct}
            />
        </Box>
    );
};

const toggleButtonStyle = {
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    whiteSpace: 'nowrap',
    flexShrink: 0
};

SimilarProductCarousel.propTypes = {
    isSmallView: PropTypes.bool,
    currentProduct: PropTypes.object,
    simplifiedCompareVersion: PropTypes.string
};

export default wrapFunctionalComponent(SimilarProductCarousel, 'SimilarProductCarousel');
